"use client";

import { useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { AdminDialog } from "@/components/admin/admin-dialog-layout";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "@/components/ui/input-group";
import { Progress } from "@/components/ui/progress";
import { CloudUploadIcon } from "lucide-react";
import { getCachedUploadRules } from "./utils";
import { uploadAttachWithProgress } from "@/lib/upload/attach-upload-client";
import { buildAcceptAttribute, getFileExtension, isUploadFileAllowed, isUploadKind } from "@/lib/upload/kinds";
import type { AttachFileRecord } from "./types";

export function AttachReplaceDialog({ file, onClose, onReplaced }: {
  file: AttachFileRecord | null;
  onClose: () => void;
  onReplaced: (file: AttachFileRecord) => Promise<void>;
}) {

  return <ReplaceForm key={file?.id ?? "closed"} target={file} onClose={onClose} onReplaced={onReplaced} />;
}

function ReplaceForm({ target, onClose, onReplaced }: {
  target: AttachFileRecord | null;
  onClose: () => void;
  onReplaced: (file: AttachFileRecord) => Promise<void>;
}) {
  const t = useTranslations("site.attach");
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<"uploading" | "saving">("uploading");
  const extension = target ? getFileExtension(target.fileName) : "";

  function chooseFile() {
    if (!saving) fileInputRef.current?.click();
  }

  function selectFile(candidate: File | undefined) {
    if (!candidate || !target || saving) return;
    const cachedRules = getCachedUploadRules(target.kind, target.module ?? undefined);
    const rules = { extensions: [extension], maxSizeMb: cachedRules?.maxSizeMb ?? Number.POSITIVE_INFINITY };
    const check = isUploadFileAllowed(candidate.name, candidate.size, rules);
    if (!check.ok) {
      toast.error(check.reason === "empty" ? t("fileRequired") : check.reason === "tooLarge"
        ? t("fileTooLarge", { max: rules.maxSizeMb })
        : t("onlySupports", { extensions: buildAcceptAttribute(rules.extensions) }));
      return;
    }
    setFile(candidate);
  }

  async function replace() {
    if (!target || !file || saving) return;
    setSaving(true);
    try {
      const updated = await uploadAttachWithProgress({
        file, replaceId: target.id, kind: isUploadKind(target.kind) ? target.kind : "image",
        onProgress: setProgress,
        onPhase: setPhase,
        messages: { failed: t("uploadFailedRetry"), network: t("networkError") },
      });
      toast.success(t("replaced"));
      await onReplaced(updated);
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("uploadFailedRetry"));
    } finally { setSaving(false); }
  }

  return <AdminDialog open={!!target} onOpenChange={(open) => { if (!open && !saving) onClose(); }}
    title={t("overwriteFile")} size="sm" saving={saving}
    confirm={{ dirty: !!file, label: t("overwriteFile"), onClick: () => void replace() }}>
    <div className="space-y-3">
      <p className="break-all text-sm">{target?.originalName}</p>
      <p id="attach-replace-hint" className="text-xs text-muted-foreground">{t("replaceHint", { extension })}</p>
      <input ref={fileInputRef} type="file" className="hidden" disabled={saving}
        aria-label={t("chooseFile")} aria-describedby="attach-replace-hint"
        accept={extension ? buildAcceptAttribute([extension]) : undefined}
        onChange={(event) => { selectFile(event.target.files?.[0]); event.target.value = ""; }} />
      <InputGroup>
        <InputGroupInput readOnly value={file?.name ?? ""} placeholder={t("chooseFile")}
          disabled={saving} className="cursor-pointer truncate" onClick={chooseFile}
          aria-label={t("chooseFile")} aria-describedby="attach-replace-hint"
          onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); chooseFile(); } }} />
        <InputGroupAddon align="inline-end" variant="icon">
          <InputGroupButton size="icon-xs" disabled={saving} onClick={chooseFile}
            aria-label={t(file ? "replaceFile" : "chooseFile")}>
            <CloudUploadIcon className="size-4" />
          </InputGroupButton>
        </InputGroupAddon>
      </InputGroup>
      {saving && <div className="space-y-1"><Progress value={progress} />{phase === "saving" && <p role="status" className="text-xs text-muted-foreground">{t("savingUpload")} {progress}%</p>}</div>}
    </div>
  </AdminDialog>;
}
